import { Box, Paper, Typography } from "@mui/material";
import {
  GoogleMap,
  Libraries,
  MarkerF,
  useLoadScript,
} from "@react-google-maps/api";
import React, { useCallback, useState } from "react";

const LOCATION_INPUT_LIBRARIES: Libraries = ["places", "geocoding", "marker"];

interface LocationInputProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number, address?: string) => void;
  onMapLoad?: (map: google.maps.Map) => void;
}

const LocationInput: React.FC<LocationInputProps> = ({
  latitude,
  longitude,
  onLocationChange,
  onMapLoad,
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LOCATION_INPUT_LIBRARIES,
  });

  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<{
    lat: number;
    lng: number;
  }>({ lat: latitude, lng: longitude });

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setGeocoder(new google.maps.Geocoder());
      if (onMapLoad) {
        onMapLoad(map);
      }
    },
    [onMapLoad]
  );

  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    if (!geocoder) return;

    try {
      const response = await geocoder.geocode({
        location: { lat, lng },
      });

      if (response.results[0]) {
        return response.results[0].formatted_address;
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
    return undefined;
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setSelectedPosition({ lat, lng });
      const address = await getAddressFromCoordinates(lat, lng);
      onLocationChange(lat, lng, address);
    }
  };

  // Update marker position when props change
  React.useEffect(() => {
    setSelectedPosition({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  if (!isLoaded) {
    return (
      <Paper
        sx={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading map...
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: 300, width: "100%", position: "relative" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={selectedPosition}
        zoom={12}
        onLoad={onLoad}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          draggableCursor: "crosshair",
          draggingCursor: "crosshair",
        }}
      >
        <MarkerF
          position={selectedPosition}
          animation={google.maps.Animation.DROP}
        />
      </GoogleMap>
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          bgcolor: "background.paper",
          p: 1,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Click on the map to set the shop location
        </Typography>
      </Box>
    </Box>
  );
};

export default LocationInput;
